import React from "react";
import { Stack, Text, TooltipHost, Icon } from "@fluentui/react";
import { CustomMetricsCard } from "../../../../SharedComponents/CustomMetricsCard";
import { CatalystTabTooltips } from "../../Common/HealthView.constants";

export interface CatalystBowlersMetricsProps {
  title: string;
  catalystPercentage: number;
  sentimentUpgradeCount: number;
  sentimentDowngradeCount: number;
  focusRedCount?: number;
  focusGreenCount?: number;
  isLoading?: boolean;
  onMetricClick?: (metricLabel: string) => void;
  isMetricClickable?: (metricLabel: string) => boolean;
}

const METRIC_COLORS = {
  catalystPercent: "#0078D4",
  sentimentUpgrade: "#038387",
  sentimentDowngrade: "#D77440",
  focusRed: "#B4009E",
  focusGreen: "#038387",
};

export const CatalystBowlersMetrics: React.FC<CatalystBowlersMetricsProps> = ({
  title,
  catalystPercentage,
  sentimentUpgradeCount,
  sentimentDowngradeCount,
  focusRedCount,
  focusGreenCount,
  isLoading,
  onMetricClick,
  isMetricClickable,
}) => {
  const getMetricClickHandler = (metricLabel: string) => {
    if (!onMetricClick || (isMetricClickable && !isMetricClickable(metricLabel))) {
      return undefined;
    }
    return () => onMetricClick(metricLabel);
  };

  return (
    <Stack tokens={{ childrenGap: 12 }} styles={{ root: { paddingRight: 24 } }}>
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
        <Text
          variant="mediumPlus"
          styles={{ root: { fontWeight: 600, display: "block" } }}
        >
          {title}
        </Text>
        <TooltipHost content={CatalystTabTooltips.BowlersHeader}>
          <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
        </TooltipHost>
      </Stack>
      <Stack tokens={{ childrenGap: 6 }}>
        {/* Row 1: 3 cards */}
        <Stack horizontal tokens={{ childrenGap: 6 }}>
          <Stack.Item grow>
            <CustomMetricsCard
              title="% Customers covered by Catalyst"
              description={CatalystTabTooltips.Metrics["% Customers covered by Catalyst"]}
              currentValue={`${Math.round(catalystPercentage)}%`}
              color={METRIC_COLORS.catalystPercent}
              disableSparkline={true}
              onClick={getMetricClickHandler("% Customers covered by Catalyst")}
            />
          </Stack.Item>
          <Stack.Item grow>
            <CustomMetricsCard
              title="# Customer Sentiment Upgrade (s)"
              description={CatalystTabTooltips.Metrics["# Customer Sentiment Upgrade (s)"]}
              currentValue={sentimentUpgradeCount}
              color={METRIC_COLORS.sentimentUpgrade}
              disableSparkline={true}
              onClick={getMetricClickHandler("# Customer Sentiment Upgrade (s)")}
            />
          </Stack.Item>
          <Stack.Item grow>
            <CustomMetricsCard
              title="# Customer Sentiment Downgrade (s)"
              description={CatalystTabTooltips.Metrics["# Customer Sentiment Downgrade (s)"]}
              currentValue={sentimentDowngradeCount}
              color={METRIC_COLORS.sentimentDowngrade}
              disableSparkline={true}
              onClick={getMetricClickHandler("# Customer Sentiment Downgrade (s)")}
            />
          </Stack.Item>
        </Stack>
        {/* Row 2: 2 cards */}
        <Stack horizontal tokens={{ childrenGap: 6 }}>
          <Stack.Item grow>
            <CustomMetricsCard
              title="# Focus customers 'red' in support health"
              description={CatalystTabTooltips.Metrics["# Focus customers 'red' in support health"]}
              currentValue={focusRedCount ?? "TBD"}
              color={METRIC_COLORS.focusRed}
              disableSparkline={true}
              onClick={getMetricClickHandler("# Focus customers 'red' in support health")}
            />
          </Stack.Item>
          <Stack.Item grow>
            <CustomMetricsCard
              title="# Focus customers 'green' in support health"
              description={CatalystTabTooltips.Metrics["# Focus customers 'green' in support health"]}
              currentValue={focusGreenCount ?? "TBD"}
              color={METRIC_COLORS.focusGreen}
              disableSparkline={true}
              onClick={getMetricClickHandler("# Focus customers 'green' in support health")}
            />
          </Stack.Item>
        </Stack>
      </Stack>
    </Stack>
  );
};
